import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import Chip from "@/components/ui/chip";
import Text from "@/components/ui/text";
import IconContainer from "@/components/ui/icon-container";
import { Add } from "@dreamhub/designsystem/icons";
import { renderTaskLinkIcon } from "@/components/layouts/TasksDrawer/taskLinkTypes";
import { TaskLink } from "@/entities/tasks/types";
import LinkToSearchPanel from "@/components/layouts/TasksDrawer/LinkToSearchPanel";
import {
  EntityType,
  entityTypeToEntityName
} from "@/services/hooks/entities/entityTypes";

const TRANSLATION_KEY = "tasks.createModal";

const getRecordPath = ({ entityType, entityId }: TaskLink): string | null =>
  entityType === undefined || entityType === EntityType.USER
    ? null
    : `/${entityTypeToEntityName[entityType]}/${entityId}`;

interface Props {
  links: TaskLink[];
  suggestions: TaskLink[];
  originName?: string;
  onAdd: (link: TaskLink) => void;
  onRemove: (entityId: string) => void;
}

const LinkedToField: React.FC<Props> = ({
  links,
  suggestions,
  originName,
  onAdd,
  onRemove
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div
      className="flex items-center gap-2 flex-wrap min-w-0 max-h-[6.25rem] overflow-y-auto"
      data-test="linked-to-field"
    >
      {links.map((link) => {
        const label = link.entityName || link.entityId;
        const recordPath = getRecordPath(link);
        return (
          <Chip
            key={link.entityId}
            size="md"
            filled
            removable
            removeAriaLabel={t(`${TRANSLATION_KEY}.removeLink`, { name: label })}
            onRemove={() => onRemove(link.entityId)}
            icon={renderTaskLinkIcon(link.entityType)}
            label={label}
            data-test={`linked-to-chip-${link.entityId}`}
          >
            {recordPath && (
              <a
                href={recordPath}
                target="_blank"
                rel="noopener noreferrer"
                className="min-w-0 truncate hover:underline"
                data-test={`linked-to-link-${link.entityId}`}
              >
                <Text variant="smBody" hasTruncatedTooltip className="truncate">
                  {label}
                </Text>
              </a>
            )}
          </Chip>
        );
      })}
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Chip
            size="md"
            ghost
            selected={isOpen}
            icon={
              <IconContainer size="sm" color="secondary">
                <Add />
              </IconContainer>
            }
            label={t(`${TRANSLATION_KEY}.linkTo`)}
            onClick={() => undefined}
            data-test="link-to-trigger"
          />
        </PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={4}
          className="w-fit max-w-[calc(100vw-2rem)]"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <LinkToSearchPanel
            linkedIds={links.map((link) => link.entityId)}
            suggestions={suggestions}
            originName={originName}
            onSelect={onAdd}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default LinkedToField;
